/**
 * Side insets for the multi-table navigation strip's band.
 *
 * The strip fills its band, so the band's edges must stop short of the floating
 * corner clusters that share the top of the felt — the menu / Add Chips /
 * support column on the left, and the Sit Out / Exit Table stack on the right.
 * Those clusters are content-sized (button labels; Add Chips mounts and
 * unmounts with top-up eligibility) AND offset from the screen edge by their
 * own margins, so callers pass each cluster's REACH — how far it extends inward
 * from its side of the viewport, i.e. the left cluster's right edge, and the
 * viewport width minus the right cluster's left edge. Measuring reach rather
 * than width is what makes the gap below real clearance rather than an
 * approximation that the cluster's own offset eats into.
 *
 * Pure so the geometry is unit-testable without a layout engine. A reach of 0
 * (not yet measured / no cluster / jsdom) falls back to the caller's default
 * rather than pinning the strip to the screen edge.
 */

// Clear space left between a corner cluster and the nearest strip content.
export const STRIP_CLUSTER_GAP = 12;

/**
 * Last measured cluster reach, remembered across header mounts.
 *
 * The single-table header is part of the per-table subtree, so switching tables
 * REMOUNTS it — its measurements would otherwise restart at "nothing measured",
 * paint one frame with the fallback insets (a too-wide band, so an oversized
 * strip) and then snap to the real values. On every swipe. That reads as the
 * strip zooming in and out.
 *
 * The clusters are the same physical furniture whichever table is on screen, so
 * the previous measurement is the best possible first guess: the strip paints
 * at the right size immediately and the fresh measurement usually confirms it.
 */
let remembered: { left: number; right: number } = { left: 0, right: 0 };

export const rememberClusterReach = (reach: { left: number; right: number }): void => {
  if (reach.left > 0 || reach.right > 0) remembered = reach;
};

export const lastClusterReach = (): { left: number; right: number } => remembered;

/** Test seam — drop the memory so suites don't leak measurements into each other. */
export const forgetClusterReach = (): void => {
  remembered = { left: 0, right: 0 };
};

export type StripBandInsets = { left: number; right: number };

/** Minimal rect shape — just the edges, so tests need no layout engine. */
export type EdgeRect = { left: number; right: number; width?: number };

/**
 * Cluster reach measured INSIDE the header's own overlay root, not against the
 * viewport.
 *
 * The frame matters: the single-table header is part of the per-table subtree,
 * and during a carousel slide that subtree sits in a panel translated a full
 * viewport width away. Measured against `window.innerWidth`, the off-screen
 * copy computed a reach of ~0 (or wildly negative), collapsed its band to
 * nothing, and — since the corner clusters never change size — never
 * re-measured. That is the "strip is broken after switching tables" bug.
 *
 * Distances between the root and its own children are translation-invariant, so
 * measuring within the root gives the same answer on-screen and off.
 */
export const clusterReachWithin = (
  root: EdgeRect | null | undefined,
  leftCluster: EdgeRect | null | undefined,
  rightCluster: EdgeRect | null | undefined
): { left: number; right: number } => {
  // A zero-width root means "not laid out yet" — report nothing measured so the
  // caller falls back rather than pinning the strip to an edge.
  if (!root || root.right - root.left <= 0) return { left: 0, right: 0 };
  return {
    left: leftCluster ? leftCluster.right - root.left : 0,
    right: rightCluster ? root.right - rightCluster.left : 0
  };
};

export function stripBandInsets(
  leftClusterReach: number,
  rightClusterReach: number,
  fallback: number
): StripBandInsets {
  const inset = (reach: number): number => {
    if (!Number.isFinite(reach) || reach <= 0) return fallback;
    // Never come in tighter than the fallback: a mid-animation or partially
    // mounted cluster can measure narrow, and the strip must not creep under it.
    return Math.max(fallback, Math.round(reach) + STRIP_CLUSTER_GAP);
  };
  return { left: inset(leftClusterReach), right: inset(rightClusterReach) };
}
